import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/react';
import type { Exam, Patient, Status } from '@/lib/types';
import { ExamRow } from './ExamRow';
import { NewOrderModal, type NewOrderPayload } from './NewOrderModal';

const statusColor: Record<Status, string> = {
  ok: 'text-ok-deep',
  warn: 'text-warn-deep',
  crit: 'text-crit-deep',
};

interface BaseProps {
  patient: Patient;
}

// ─────────────────────────────────────────────────────────────────────────────
// Overview

interface OverviewProps extends BaseProps {
  exams: Exam[];
  reportUrl: string;
}

const PRIORITY_ALERTS = [
  { code: 'TSH', value: '10.92 mIU/L', title: 'TSH elevada', severity: 'crit' as Status },
  { code: 'TG/ApoB', value: '1.15', title: 'Ratio TG/ApoB', severity: 'crit' as Status },
  { code: 'NHR', value: '0.087', title: 'NHR bajo', severity: 'crit' as Status },
];

const RECENT_ACTIVITY = [
  { id: 'a1', kind: 'order', title: 'Panel tiroideo ampliado solicitado', date: 'Hoy · 14:30' },
  { id: 'a2', kind: 'note', title: 'Nota: consulta de seguimiento', date: 'Hoy · 14:20' },
  { id: 'a3', kind: 'lab', title: 'Resultados Optimal Core entregados', date: '5 may · 18:22' },
  { id: 'a4', kind: 'comm', title: 'WhatsApp enviado al paciente', date: '5 may · 18:30' },
];

const ACTIVITY_DOT: Record<string, string> = {
  order: 'bg-warn',
  note: 'bg-faint',
  lab: 'bg-ok',
  comm: 'bg-faint',
};

export function OverviewSection({ patient, exams, reportUrl }: OverviewProps) {
  const lastExam = exams[0];
  const lastVital = { bp: '128/82', hr: '72', weight: '82.4', bmi: '27.2' };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-2 max-[760px]:grid-cols-1">
        <SummaryCard
          title="Estado clínico"
          highlight={`${patient.findings} hallazgo${patient.findings === 1 ? '' : 's'}`}
          accent={statusColor[patient.findingsLevel]}
          hint={
            patient.findings === 0
              ? 'Sin hallazgos abiertos'
              : patient.findingsLevel === 'crit'
                ? 'Críticos'
                : 'A observar'
          }
        />
        <SummaryCard
          title="Próxima acción"
          highlight={
            lastExam ? `Reevaluar en 12 sem.` : 'Solicitar primer panel'
          }
          hint={
            lastExam
              ? 'Sugerido por el plan de seguimiento'
              : 'Optimal Core sugerido'
          }
        />
        <SummaryCard
          title="Última evaluación"
          highlight={
            lastExam
              ? `${parseInt(lastExam.collectedDay, 10)} ${lastExam.collectedMonth.toLowerCase()}`
              : '—'
          }
          hint={
            lastExam
              ? `Score ${lastExam.optimalScore} · ${lastExam.biomarkerCount} biomarcadores`
              : 'Aún sin paneles'
          }
        />
      </div>

      <div className="glass-panel p-[18px] px-5 flex flex-col gap-3">
        <header className="flex items-baseline justify-between gap-3">
          <h3 className="text-[11px] font-semibold tracking-wider uppercase text-muted m-0">
            Signos vitales · última consulta
          </h3>
          <span className="text-[11px] text-tertiary">5 may 26</span>
        </header>
        <div className="grid grid-cols-4 gap-3 max-[760px]:grid-cols-2">
          <VitalCell label="Presión arterial" value={lastVital.bp} unit="mmHg" />
          <VitalCell label="Frec. cardíaca" value={lastVital.hr} unit="lpm" />
          <VitalCell label="Peso" value={lastVital.weight} unit="kg" />
          <VitalCell label="IMC" value={lastVital.bmi} unit="" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 max-[880px]:grid-cols-1">
        <section className="glass-panel p-[18px] px-5 flex flex-col gap-3">
          <header className="flex items-baseline justify-between gap-3">
            <h3 className="text-[11px] font-semibold tracking-wider uppercase text-muted m-0">
              Alertas prioritarias
            </h3>
            <Link
              to={reportUrl}
              className="text-[11px] font-medium text-tertiary hover:text-primary transition-colors"
            >
              Ver reporte →
            </Link>
          </header>
          <ul className="list-none m-0 p-0 flex flex-col gap-2">
            {PRIORITY_ALERTS.map((a) => (
              <li
                key={a.code}
                className="flex items-center gap-3"
              >
                <span
                  className={`grid place-items-center w-5 h-5 rounded-full text-[11px] font-bold shrink-0 ${
                    a.severity === 'crit'
                      ? 'bg-crit-tint text-crit-deep'
                      : 'bg-warn-tint text-warn-deep'
                  }`}
                  aria-hidden="true"
                >
                  !
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold tracking-[-0.005em] truncate">
                    {a.title}
                  </div>
                </div>
                <span className={`text-[12px] font-bold tnum ${statusColor[a.severity]}`}>
                  {a.value}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="glass-panel p-[18px] px-5 flex flex-col gap-3">
          <header className="flex items-baseline justify-between gap-3">
            <h3 className="text-[11px] font-semibold tracking-wider uppercase text-muted m-0">
              Actividad reciente
            </h3>
          </header>
          <ul className="list-none m-0 p-0 flex flex-col gap-2.5">
            {RECENT_ACTIVITY.map((a) => (
              <li key={a.id} className="flex items-start gap-2.5">
                <span
                  className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${ACTIVITY_DOT[a.kind]}`}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] text-secondary truncate">
                    {a.title}
                  </div>
                  <div className="text-[11px] text-muted tnum">{a.date}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function VitalCell({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted">
        {label}
      </span>
      <span className="text-[18px] font-bold tracking-[-0.018em] tnum leading-none text-primary">
        {value}{' '}
        {unit && (
          <span className="text-[11px] text-tertiary font-normal">{unit}</span>
        )}
      </span>
    </div>
  );
}

interface ExamsSectionProps extends BaseProps {
  exams: Exam[];
  reportUrl: string;
}

export function ExamsSection({ exams, reportUrl }: ExamsSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={`Exámenes (${exams.length})`}
        action={
          <Link
            to={reportUrl}
            className="text-[12px] font-medium text-primary bg-transparent border border-line-strong rounded-md px-2.5 py-1.5 cursor-pointer transition-colors hover:bg-soft hover:border-line-default"
          >
            Abrir Optimal Core →
          </Link>
        }
      />
      {exams.length > 0 ? (
        <div className="glass-panel">
          {exams.map((exam) => (
            <ExamRow key={exam.id} exam={exam} reportUrl={reportUrl} />
          ))}
        </div>
      ) : (
        <Empty>Este paciente todavía no tiene exámenes.</Empty>
      )}
    </div>
  );
}

export function ImagingsSectionPlaceholder() {
  return (
    <div className="flex flex-col gap-3">
      <SectionHeader title="Imágenes" />
      <div className="glass-panel py-12 px-6 flex flex-col items-center gap-3 text-center">
        <span className="grid place-items-center w-12 h-12 rounded-full bg-soft text-tertiary">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </span>
        <h3 className="text-[15px] font-semibold tracking-[-0.005em] m-0">
          Visualizador de imágenes
        </h3>
        <p className="text-[13px] text-tertiary m-0 max-w-[360px] leading-[1.55]">
          Pronto podrás revisar DXA, ecografías y resonancias directamente desde
          el perfil del paciente, con anotaciones y comparativas.
        </p>
        <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted bg-soft px-2 py-1 rounded-full mt-1">
          Coming soon
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notes

export interface Note {
  id: string;
  date: string;
  author: string;
  title: string;
  body: string;
}

export const INITIAL_NOTES: Note[] = [
  {
    id: 'n3',
    date: '5 may 26',
    author: 'Dr. Andrés Vásquez',
    title: 'Consulta de seguimiento',
    body: 'Paciente refiere fatiga matutina. Se identifica TSH elevada (10.92 mIU/L) compatible con hipotiroidismo subclínico. Se solicita panel tiroideo ampliado (T4 libre, anti-TPO) y se inicia referencia a endocrinología.',
  },
  {
    id: 'n2',
    date: '12 feb 26',
    author: 'Dr. Andrés Vásquez',
    title: 'Primera consulta · plan metabólico',
    body: 'Se establece plan metabólico de 12 semanas: omega-3, reducción de carbohidratos refinados, 3-4 sesiones de ejercicio aeróbico semanal. Adherencia esperada alta.',
  },
  {
    id: 'n1',
    date: '20 ene 26',
    author: 'Dra. Carolina Méndez · Nutrición',
    title: 'Evaluación nutricional inicial',
    body: 'IMC 27.4. Patrón alimentario con alto consumo de ultraprocesados. Se entrega plan de alimentación basado en mediterránea modificada.',
  },
];

export interface NotesSectionProps {
  notes: Note[];
  onAddNote: (note: Note) => void;
  splitMode: boolean;
  onToggleSplit: () => void;
}

export function NotesSection({ notes, onAddNote, splitMode, onToggleSplit }: NotesSectionProps) {
  if (splitMode) {
    return (
      <div className="glass-panel py-12 px-6 flex flex-col items-center gap-3 text-center">
        <span className="grid place-items-center w-12 h-12 rounded-full bg-soft text-tertiary">
          <SplitIcon size={22} />
        </span>
        <h3 className="text-[15px] font-semibold tracking-[-0.005em] m-0">
          Notas en modo split
        </h3>
        <p className="text-[13px] text-tertiary m-0 max-w-[420px] leading-[1.55]">
          El panel de notas está abierto al lado para tomar apuntes mientras
          navegas el resto del expediente.
        </p>
        <button
          type="button"
          onClick={onToggleSplit}
          className="inline-flex items-center gap-1 text-[12.5px] font-medium text-secondary bg-transparent border border-line-strong rounded-md px-3 py-1.5 cursor-pointer transition-colors hover:bg-soft hover:border-line-default hover:text-primary mt-1"
        >
          Cerrar split y volver al tab
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={`Notas clínicas (${notes.length})`}
      />

      <NoteEditor onSave={onAddNote} />

      <ul className="list-none m-0 p-0 flex flex-col gap-2">
        {notes.map((n) => (
          <li
            key={n.id}
            className="glass-panel px-5 py-4 flex flex-col gap-1.5"
          >
            <header className="flex items-baseline justify-between gap-3 flex-wrap">
              <h4 className="text-[14px] font-semibold tracking-[-0.005em] m-0">
                {n.title}
              </h4>
              <span className="text-[11px] text-tertiary tnum">{n.date}</span>
            </header>
            <p className="text-[12px] text-muted m-0 tracking-[0.005em]">
              {n.author}
            </p>
            <p className="text-[13px] text-secondary m-0 leading-[1.55] whitespace-pre-line">
              {n.body}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface NoteEditorProps {
  onSave: (note: Note) => void;
  compact?: boolean;
  onOrder?: () => void;
}

function NoteEditor({ onSave, compact, onOrder }: NoteEditorProps) {
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [refining, setRefining] = useState(false);

  const reset = () => {
    setDraftTitle('');
    setDraftBody('');
  };

  const handleSave = () => {
    const title = draftTitle.trim();
    const body = draftBody.trim();
    if (!title || !body) return;
    const today = new Date();
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const date = `${today.getDate()} ${months[today.getMonth()]} ${(today.getFullYear() % 100).toString().padStart(2, '0')}`;
    onSave({
      id: `n-${Date.now()}`,
      date,
      author: 'Dr. Andrés Vásquez',
      title,
      body,
    });
    reset();
  };

  const handleRefine = () => {
    const body = draftBody.trim();
    if (!body || refining) return;
    setRefining(true);
    window.setTimeout(() => {
      setDraftBody(refineMockText(body));
      setRefining(false);
    }, 1100);
  };

  const canSave = draftTitle.trim().length > 0 && draftBody.trim().length > 0;

  return (
    <div className={`glass-panel flex flex-col gap-2.5 ${compact ? 'p-3' : 'p-4 px-5'}`}>
      <input
        type="text"
        placeholder="Título de la nota"
        value={draftTitle}
        onChange={(e) => setDraftTitle(e.target.value)}
        className={`font-inherit font-semibold text-primary tracking-[-0.005em] border border-line rounded-md bg-subtle px-3 py-2 outline-none transition-colors w-full placeholder:text-faint focus:bg-surface focus:border-line-default ${compact ? 'text-[13px]' : 'text-[14px]'}`}
      />
      <textarea
        rows={compact ? 8 : 4}
        placeholder="Hallazgos, plan, indicaciones, observaciones a medida que avanza la consulta…"
        value={draftBody}
        onChange={(e) => setDraftBody(e.target.value)}
        disabled={refining}
        className={`font-inherit text-[13px] text-primary border border-line rounded-md bg-subtle px-3 py-2.5 resize-y outline-none transition-colors leading-[1.55] w-full placeholder:text-faint focus:bg-surface focus:border-line-default disabled:opacity-70 ${refining ? 'ai-shimmer ai-loading-pulse' : ''}`}
      />
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={handleRefine}
          disabled={!draftBody.trim() || refining}
          title="Refinar redacción con Optimal AI"
          className="ai-action-button inline-flex items-center gap-1.5 text-[12px] font-medium text-primary border rounded-md px-2.5 py-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SparkleIcon /> {refining ? 'Refinando…' : 'Refinar con Optimal AI'}
        </button>
        {onOrder && (
          <button
            type="button"
            onClick={onOrder}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-secondary bg-transparent border border-line-strong rounded-md px-2.5 py-1.5 cursor-pointer transition-colors hover:bg-soft hover:border-line-default hover:text-primary"
          >
            <span aria-hidden="true">+</span> Generar orden
          </button>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={reset}
          disabled={!draftTitle && !draftBody}
          className="text-[12.5px] font-medium text-tertiary bg-transparent border-0 px-2.5 py-1.5 rounded-md cursor-pointer transition-colors hover:text-primary disabled:opacity-50"
        >
          Limpiar
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="font-inherit text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary text-surface border-0 cursor-pointer transition-opacity hover:opacity-90 active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Guardar nota
        </button>
      </div>
    </div>
  );
}

function refineMockText(text: string): string {
  const t = text.trim();
  if (!t) return t;
  // Heuristic mock: capitalize, split into structured sentences with bullets
  // when the input is unstructured prose.
  const sentences = t
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .filter(Boolean);
  if (sentences.length <= 1) {
    return `Hallazgo principal: ${capitalize(t.replace(/\.$/, ''))}.\n\nPlan sugerido: continuar evaluación según protocolo y reagendar control en 12 semanas.`;
  }
  return sentences
    .map((s) => `• ${capitalize(s.trim().replace(/\.$/, ''))}.`)
    .join('\n');
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface NotesSplitPanelProps {
  notes: Note[];
  onAddNote: (note: Note) => void;
  onClose: () => void;
  patientName: string;
}

export function NotesSplitPanel({ notes, onAddNote, onClose, patientName }: NotesSplitPanelProps) {
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  const handleCreateOrder = (payload: NewOrderPayload) => {
    const order = `${payload.title}${payload.notes ? ` · ${payload.notes}` : ''}`;
    const today = new Date();
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const date = `${today.getDate()} ${months[today.getMonth()]} ${(today.getFullYear() % 100).toString().padStart(2, '0')}`;
    onAddNote({
      id: `n-${Date.now()}`,
      date,
      author: 'Dr. Andrés Vásquez',
      title: `Orden generada · ${payload.title}`,
      body: `Se generó una nueva orden desde el panel de notas: ${order}`,
    });
  };

  const splitTabClass = ({ selected }: { selected: boolean }) =>
    [
      'flex-1 font-inherit text-[12.5px] py-2 px-2 cursor-pointer outline-none transition-colors rounded-md',
      selected
        ? 'bg-primary text-surface font-semibold'
        : 'bg-transparent text-tertiary font-medium hover:text-primary hover:bg-soft',
    ].join(' ');

  return (
    <aside className="fixed top-[52px] bottom-0 right-0 w-[400px] max-[1024px]:w-[340px] max-[760px]:w-full bg-surface border-l border-line z-40 flex flex-col shadow-elevated">
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-line shrink-0">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted">
            Notas en consulta
          </span>
          <span className="text-[13px] font-medium text-primary truncate">
            {patientName}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar panel"
          className="text-[13px] w-7 h-7 border border-line bg-transparent text-tertiary rounded-sm cursor-pointer transition-colors hover:bg-soft hover:text-primary shrink-0"
        >
          ✕
        </button>
      </header>

      <TabGroup className="flex-1 flex flex-col min-h-0">
        <TabList className="flex gap-1 p-1.5 mx-3 mt-3 rounded-md border border-line shrink-0">
          <Tab className={splitTabClass}>Nueva nota</Tab>
          <Tab className={splitTabClass}>
            Previas{' '}
            <span className="text-[10.5px] font-semibold tnum opacity-70 ml-0.5">
              ({notes.length})
            </span>
          </Tab>
        </TabList>

        <TabPanels className="flex-1 min-h-0 flex flex-col">
          <TabPanel className="outline-none flex-1 min-h-0 overflow-y-auto p-3">
            <NoteEditor
              onSave={onAddNote}
              compact
              onOrder={() => setOrderModalOpen(true)}
            />
          </TabPanel>
          <TabPanel className="outline-none flex-1 min-h-0 overflow-y-auto px-3 pb-4 flex flex-col gap-2">
            {notes.length === 0 && (
              <p className="text-[12px] text-muted m-0 text-center py-6">
                Aún no hay notas previas.
              </p>
            )}
            {notes.map((n) => (
              <div
                key={n.id}
                className="glass-panel px-3 py-2.5 flex flex-col gap-1"
              >
                <header className="flex items-baseline justify-between gap-2">
                  <h5 className="text-[12.5px] font-semibold tracking-[-0.005em] m-0 truncate">
                    {n.title}
                  </h5>
                  <span className="text-[10.5px] text-tertiary tnum shrink-0">
                    {n.date}
                  </span>
                </header>
                <p className="text-[11.5px] text-muted m-0">{n.author}</p>
                <p className="text-[12px] text-secondary m-0 leading-[1.5] whitespace-pre-line line-clamp-4">
                  {n.body}
                </p>
              </div>
            ))}
          </TabPanel>
        </TabPanels>
      </TabGroup>

      <NewOrderModal
        open={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        onCreate={handleCreateOrder}
      />
    </aside>
  );
}

function SplitIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M14 4v16" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 1.5l1.4 4.1 4.1 1.4-4.1 1.4L8 12.5 6.6 8.4 2.5 7l4.1-1.4L8 1.5z" />
      <path d="M13 11.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Documents

const DOCUMENTS = [
  { id: 'd1', title: 'Consentimiento informado', type: 'PDF', size: '120 KB', date: '20 ene 26' },
  { id: 'd2', title: 'Plan de alimentación · mediterránea', type: 'PDF', size: '340 KB', date: '20 ene 26' },
  { id: 'd3', title: 'Reporte panel core · 5 may', type: 'PDF', size: '2.1 MB', date: '5 may 26' },
  { id: 'd4', title: 'Resumen para endocrinología', type: 'PDF', size: '180 KB', date: '6 may 26' },
];

export function DocumentsSection() {
  return (
    <div className="flex flex-col gap-3">
      <SectionHeader title={`Documentos (${DOCUMENTS.length})`} action="Subir documento" />
      <div className="glass-panel">
        {DOCUMENTS.map((d, i) => (
          <div
            key={d.id}
            className={`flex items-center gap-4 px-5 py-3 ${i < DOCUMENTS.length - 1 ? 'border-b border-line' : ''}`}
          >
            <span className="grid place-items-center w-9 h-9 rounded-md border border-line bg-transparent text-[10px] font-semibold tracking-[0.06em] text-tertiary shrink-0">
              {d.type}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold tracking-[-0.005em] truncate">
                {d.title}
              </div>
              <div className="text-[11.5px] text-tertiary tnum">
                {d.size} · {d.date}
              </div>
            </div>
            <button
              type="button"
              className="text-[12px] font-medium text-tertiary bg-transparent border border-line rounded-md px-2.5 py-1.5 cursor-pointer transition-colors hover:bg-soft hover:text-primary hover:border-line-default"
            >
              Descargar
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vital signs

type VitalSource = 'wearable' | 'consultorio' | 'hospital';

interface VitalEntry {
  date: string;
  bp: string;
  hr: string;
  spo2: string;
  weight: string;
  height: string;
  bmi: string;
  temp: string;
  source: VitalSource;
}

const VITALS: VitalEntry[] = [
  { date: '6 may 26 · 09:14', bp: '126/80', hr: '68', spo2: '98', weight: '82.3', height: '174', bmi: '27.2', temp: '36.5', source: 'wearable' },
  { date: '5 may 26 · 14:32', bp: '128/82', hr: '72', spo2: '97', weight: '82.4', height: '174', bmi: '27.2', temp: '36.6', source: 'consultorio' },
  { date: '12 feb 26 · 10:18', bp: '132/86', hr: '76', spo2: '96', weight: '83.8', height: '174', bmi: '27.7', temp: '36.5', source: 'consultorio' },
  { date: '8 feb 26 · 22:40', bp: '142/94', hr: '88', spo2: '94', weight: '83.6', height: '174', bmi: '27.6', temp: '37.4', source: 'hospital' },
  { date: '20 ene 26 · 11:05', bp: '134/88', hr: '78', spo2: '97', weight: '84.6', height: '174', bmi: '28.0', temp: '36.7', source: 'consultorio' },
];

const SOURCE_LABEL: Record<VitalSource, string> = {
  wearable: 'Wearable',
  consultorio: 'Consultorio',
  hospital: 'Hospital · triage',
};

const SOURCE_BADGE: Record<VitalSource, string> = {
  wearable: 'bg-soft text-tertiary',
  consultorio: 'bg-soft text-secondary',
  hospital: 'bg-crit-tint text-crit-deep',
};

export function VitalsSection() {
  return (
    <div className="flex flex-col gap-3">
      <SectionHeader title="Signos vitales" action="Registrar consulta" />
      <div className="glass-panel overflow-x-auto">
        <div className="min-w-[860px]">
          <div
            aria-hidden="true"
            className="grid grid-cols-[150px_repeat(7,1fr)_120px] gap-3 items-center px-5 py-2 text-[10px] uppercase tracking-[0.06em] font-semibold text-muted bg-subtle border-b border-line"
          >
            <span>Fecha · hora</span>
            <span>Presión</span>
            <span>F.C.</span>
            <span>SpO₂</span>
            <span>Peso</span>
            <span>Talla</span>
            <span>IMC</span>
            <span>Temp.</span>
            <span>Origen</span>
          </div>
          {VITALS.map((v, i) => (
            <div
              key={v.date}
              className={`grid grid-cols-[150px_repeat(7,1fr)_120px] gap-3 items-center px-5 py-3 text-[13px] tnum ${i < VITALS.length - 1 ? 'border-b border-line' : ''}`}
            >
              <span className="text-muted">{v.date}</span>
              <span className="font-semibold">{v.bp} <span className="text-[10px] text-tertiary font-normal">mmHg</span></span>
              <span className="font-semibold">{v.hr} <span className="text-[10px] text-tertiary font-normal">lpm</span></span>
              <span className="font-semibold">{v.spo2} <span className="text-[10px] text-tertiary font-normal">%</span></span>
              <span className="font-semibold">{v.weight} <span className="text-[10px] text-tertiary font-normal">kg</span></span>
              <span className="font-semibold">{v.height} <span className="text-[10px] text-tertiary font-normal">cm</span></span>
              <span className="font-semibold">{v.bmi}</span>
              <span className="font-semibold">{v.temp} <span className="text-[10px] text-tertiary font-normal">°C</span></span>
              <span className="flex items-center gap-1.5">
                <SourceIcon source={v.source} />
                <span
                  className={`text-[10.5px] font-semibold tracking-[0.04em] uppercase px-1.5 py-0.5 rounded-sm ${SOURCE_BADGE[v.source]}`}
                >
                  {SOURCE_LABEL[v.source]}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SourceIcon({ source }: { source: VitalSource }) {
  if (source === 'wearable') {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-tertiary shrink-0">
        <rect x="6" y="6" width="12" height="14" rx="2" />
        <path d="M9 4h6M9 22h6" />
      </svg>
    );
  }
  if (source === 'hospital') {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-crit-deep shrink-0">
        <path d="M3 21h18M5 21V7l7-3 7 3v14M10 21v-5h4v5M9 11h2M13 11h2M9 14h2M13 14h2" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-tertiary shrink-0">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Communication

const COMMUNICATIONS = [
  { id: 'c1', channel: 'WhatsApp', direction: 'sent', date: '6 may · 09:14', preview: 'Te comparto el resumen para endocrinología. Cualquier duda, escríbeme.' },
  { id: 'c2', channel: 'Email', direction: 'received', date: '5 may · 18:22', preview: 'Doctor, los resultados que pidió ya están listos.' },
  { id: 'c3', channel: 'Llamada', direction: 'sent', date: '5 may · 15:48', preview: 'Llamada de 6 min · revisión de hallazgos críticos' },
  { id: 'c4', channel: 'WhatsApp', direction: 'received', date: '15 feb · 10:02', preview: 'Hola Dr., consulta sobre la dosis de omega-3.' },
];

export function CommunicationSection() {
  return (
    <div className="flex flex-col gap-3">
      <SectionHeader title={`Comunicación (${COMMUNICATIONS.length})`} action="Nuevo mensaje" />
      <div className="glass-panel">
        {COMMUNICATIONS.map((c, i) => (
          <div
            key={c.id}
            className={`flex items-start gap-4 px-5 py-3 ${i < COMMUNICATIONS.length - 1 ? 'border-b border-line' : ''}`}
          >
            <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted w-[80px] shrink-0 mt-0.5">
              {c.channel}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-secondary leading-[1.5] truncate">
                {c.preview}
              </div>
              <div className="text-[11px] text-muted mt-0.5 tnum">
                {c.direction === 'sent' ? 'Enviado' : 'Recibido'} · {c.date}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Orders

interface Order {
  id: string;
  title: string;
  items: string;
  status: 'pending' | 'in_progress' | 'scheduled' | 'completed';
  date: string;
}

const INITIAL_ORDERS: Order[] = [
  { id: 'o1', title: 'Panel tiroideo ampliado', items: 'T4 libre · Anti-TPO · Anti-Tg', status: 'pending', date: 'Solicitada hoy' },
  { id: 'o2', title: 'Ferritina', items: 'Reflejo si Hb < 13', status: 'pending', date: 'Solicitada hoy' },
  { id: 'o3', title: 'Referencia a endocrinología', items: 'Dr. Cárdenas · Hospital Norte', status: 'in_progress', date: 'Enviada 5 may' },
  { id: 'o4', title: 'Reevaluación clínica', items: 'Optimal Core · 12 semanas', status: 'scheduled', date: 'Para 30 jul' },
  { id: 'o5', title: 'Vitamina D · 25-OH', items: 'Confirmación post-suplementación', status: 'completed', date: 'Resultado 5 may' },
];

const ORDER_LABEL: Record<Order['status'], string> = {
  pending: 'Pendiente',
  in_progress: 'En curso',
  scheduled: 'Programada',
  completed: 'Completada',
};
const ORDER_BADGE: Record<Order['status'], string> = {
  pending: 'bg-warn-tint text-warn-deep',
  in_progress: 'bg-warn-tint text-warn-deep',
  scheduled: 'bg-soft text-secondary',
  completed: 'bg-ok-tint text-ok-deep',
};

export function OrdersSection() {
  const [orders, setOrders] = useState<Order[]>(INITIAL_ORDERS);
  const [modalOpen, setModalOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<Order | null>(null);

  const handleCreate = (payload: NewOrderPayload) => {
    const newOrder: Order = {
      id: `o-${Date.now()}`,
      title: payload.title,
      items: payload.notes
        ? `${payload.items} · ${payload.notes}`
        : payload.items,
      status: 'pending',
      date: 'Solicitada hoy',
    };
    setOrders((prev) => [newOrder, ...prev]);
    setHighlightedId(newOrder.id);
    setLastCreated(newOrder);
    setShowSuccess(true);
    window.setTimeout(() => setShowSuccess(false), 1800);
    window.setTimeout(() => {
      setHighlightedId((curr) => (curr === newOrder.id ? null : curr));
      setLastCreated((curr) => (curr?.id === newOrder.id ? null : curr));
    }, 5200);
  };

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={`Órdenes (${orders.length})`}
        action={
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1 text-[12.5px] font-medium text-surface bg-primary border-0 rounded-md px-3 py-1.5 cursor-pointer transition-opacity hover:opacity-90 active:translate-y-px"
          >
            <span aria-hidden="true">+</span> Nueva orden
          </button>
        }
      />
      <ul className="list-none m-0 p-0 flex flex-col gap-2">
        {orders.map((o) => {
          const isNew = o.id === highlightedId;
          return (
            <li
              key={o.id}
              className={`glass-panel px-5 py-3.5 flex items-center gap-4 max-[760px]:flex-col max-[760px]:items-stretch ${
                isNew ? 'order-new-glow' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[13.5px] font-semibold tracking-[-0.005em] truncate">
                    {o.title}
                  </div>
                  {isNew && (
                    <span className="text-[10px] font-semibold tracking-[0.06em] uppercase px-1.5 py-0.5 rounded-full text-[rgb(186,230,253)] border border-[rgba(125,211,252,0.45)]">
                      Nueva
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-tertiary mt-0.5 truncate">
                  {o.items}
                </div>
              </div>
              <span className="text-[11px] text-muted tnum max-[760px]:order-last">
                {o.date}
              </span>
              <span
                className={`text-[10.5px] font-semibold tracking-[0.06em] uppercase px-2 py-0.5 rounded-full shrink-0 ${ORDER_BADGE[o.status]}`}
              >
                {ORDER_LABEL[o.status]}
              </span>
            </li>
          );
        })}
      </ul>

      <NewOrderModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreate}
      />

      {showSuccess && lastCreated && (
        <div
          aria-live="polite"
          className="fixed inset-0 z-[200] grid place-items-center pointer-events-none"
        >
          <div className="ai-success-bloom absolute inset-0" />
          <div className="ai-success-rise relative flex flex-col items-center gap-3 text-primary">
            <span className="ai-check-pop grid place-items-center w-14 h-14 rounded-full border border-line-default text-primary">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
            <span className="text-[22px] font-medium tracking-[-0.018em]">
              Orden creada
            </span>
            <span className="text-[12.5px] text-tertiary max-w-[320px] text-center truncate">
              {lastCreated.title}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers

interface SectionHeaderProps {
  title: string;
  action?: React.ReactNode;
}

function SectionHeader({ title, action }: SectionHeaderProps) {
  return (
    <header className="flex items-baseline justify-between gap-3">
      <h3 className="text-[15px] font-semibold tracking-[-0.01em] m-0">
        {title}
      </h3>
      {action &&
        (typeof action === 'string' ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[12.5px] font-medium text-surface bg-primary border-0 rounded-md px-3 py-1.5 cursor-pointer transition-opacity hover:opacity-90 active:translate-y-px"
          >
            <span aria-hidden="true">+</span> {action}
          </button>
        ) : (
          action
        ))}
    </header>
  );
}

interface SummaryCardProps {
  title: string;
  highlight: string;
  hint: string;
  accent?: string;
}

function SummaryCard({ title, highlight, hint, accent }: SummaryCardProps) {
  return (
    <div className="glass-panel px-4 py-3.5 flex flex-col gap-1">
      <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted">
        {title}
      </span>
      <span className={`text-[18px] font-semibold tracking-[-0.015em] ${accent ?? 'text-primary'}`}>
        {highlight}
      </span>
      <span className="text-[12px] text-tertiary">{hint}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="glass-panel py-6 text-center text-muted text-[13px]">
      {children}
    </div>
  );
}
